import type { ChartBaseProps } from '../../../molecules/ChartFrame/chartProps';
import { Card } from '../../../molecules/Card/Card';
import type { ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { seriesColor } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Search } from '../../../molecules/Search/Search';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';
import styles from './KeywordMentionsFrequencyCard.module.css';

const SERIES = [{ key: 'mentions', label: 'Mentions', color: 'chart-1' as const }];

export type KeywordMentionsFrequencyView = {
  /** The page on screen, ten bars at most: `{ [xKey]: label, mentions }`. */
  rows: ChartRow[];
  xKey: string;
  /** Per bar: a keyword *related* to the search rather than a match (drawn faded). */
  muted: boolean[];
  /**
   * The value axis, scaled over **every hit**, not the ten on screen — so a
   * page turn is a scroll down one chart instead of a jump between five.
   */
  domain: [number, number];
  ticks: number[];
  /** The page actually shown — the requested one, clamped to `pageCount`. */
  page: number;
  pageCount: number;
  /** Whether any hit is a related keyword (the match/related key shows only then). */
  related: boolean;
  /** Whether the query (or the period) left any keyword at all. */
  hasHits: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type KeywordMentionsFrequencyCardProps = WidgetBlockProps & {
  view?: KeywordMentionsFrequencyView;
  /** The search box's value. Controlled: the search runs upstream, on the full list. */
  search: string;
  /** Typing; the owner returns to page 1 on a new query. */
  onSearchChange: (next: string) => void;
  onPageChange: (page: number) => void;
  /** Opens the conversations behind a bar (index into the page's `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Trends' `Keyword mentions & frequency` — Figma `2125:89185` (was `2030:25801`
 * until the 2026-09-21 update replaced the instance), added to the frame
 * 2026-09-18.
 *
 * Every keyword, ten bars to a page, ranked by how many mentions contained it.
 * One series of mention counts: "mentions & frequency" is read as one quantity
 * (settled with the user), and the sentiment split is already the card beside
 * it.
 *
 * **The search** sits in the header's actions, where the frame puts it. Typing
 * narrows the chart to the keywords the query matches in either script, then
 * adds the keywords **related** to them, meaning the ones people use when they
 * talk about the same topics. Matches draw at full strength and related words
 * faded, with a key naming the two while a search is active. That key is an
 * addition, because without it a faded bar is a difference nobody can explain.
 *
 * **Search and paging are client-side over the full keyword list** (the
 * contract returns every keyword with its topic split), so typing never waits
 * on the network. This block is controlled: the owner holds the query and the
 * page, and hands in the page to draw.
 *
 * **Pagination** is in the legend slot, as the frame draws it, and since the
 * 2026-09-21 update it is the card's bottom edge: no box around it and no card
 * padding below it. See the stylesheet. A new query returns to page 1, and the
 * page is clamped so a filter that shortens the list can't leave the reader on
 * an empty page.
 *
 * **No KPI row**: the frame's `KPI` instance is hidden.
 */
export function KeywordMentionsFrequencyCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  search,
  onSearchChange,
  onPageChange,
  onViewDetails,
}: KeywordMentionsFrequencyCardProps) {
  const query = search.trim();
  const searching = query !== '';
  const hasData = view !== undefined && view.hasHits;
  const shell = cardState(status, hasData, onRetry);

  return (
    <Card
      {...chrome}
      /*
       * Flush only while the pager is drawn. Loading and empty drop the legend
       * slot, and without the padding their body would sit on the card's edge.
       */
      className={[spanClass(span), styles.sized, shell.state === 'default' && styles.flush]
        .filter(Boolean)
        .join(' ')}
      emptyLabel={searching ? `No keyword matches “${query}”` : 'No keywords in this period'}
      {...shell}
      title="Keyword mentions & frequency"
      helpText="Shows how many mentions contain each keyword, ranked from highest to lowest. Search in Bangla or English; lighter bars show related keywords used around the same topics."
      actions={
        <Search
          className={styles.search}
          aria-label="Search keywords"
          placeholder="Search keywords..."
          value={search}
          onValueChange={onSearchChange}
        />
      }
      chart={
        view && (
          <BarChart
            data={view.rows}
            onViewDetails={onViewDetails}
            xKey={view.xKey}
            series={SERIES}
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            /* `yDomain`/`yTicks` name the value axis whichever way the bars run —
               on this rotated plot they land on the horizontal one. */
            yDomain={view.domain}
            yTicks={view.ticks}
            mutedCells={view.muted}
            formatValue={view.formatValue}
            label={
              searching
                ? `Keywords matching “${query}” and related keywords, by mentions. Page ${view.page} of ${view.pageCount}.`
                : `Keywords by mentions. Page ${view.page} of ${view.pageCount}.`
            }
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={
        view && (
          <div className={styles.footer}>
            {/* The pager first, so the key paints over its empty left side. */}
            <Pagination
              className={styles.pager}
              page={view.page}
              pageCount={view.pageCount}
              onPageChange={onPageChange}
              label="Keyword pages"
            />
            {searching && view.related && (
              <ChartLegend
                className={styles.key}
                items={[
                  { label: 'Matches your search', color: seriesColor(0, 'chart-1') },
                  { label: 'Related keywords', color: seriesColor(0, 'chart-1'), muted: true },
                ]}
              />
            )}
          </div>
        )
      }
    />
  );
}
